import React, { memo, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { blockExportMetadata, blockInfoGraphicType, blockSettings, customizedBlock } from 'venn-state';

import type { Analysis } from 'venn-api';
import { getAnalyzedSubjectFromRequestSubject } from 'venn-utils';
import { useAppPrintMode } from '../../../print';
import { type AnalysisRequest, type ExportMetaData, useResponseParser } from '../../../studio-blocks';
import PieChart from '../../../studio-blocks/components/charts/PieChart';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import { useSubjectColors } from '../../../studio-blocks/logic/useSubjectColors';
import { useBlockContext } from '../../contexts/block-context';
import { useFilteredRequestsAndAnalyses } from '../../utils/hooks/use-filtered-requests';

interface PortfolioCompositionContentProps {
  id: string;
}

export const PortfolioCompositionContent = ({ id }: PortfolioCompositionContentProps) => {
  const { requests, analyses } = useFilteredRequestsAndAnalyses(id);
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(id));
  const exportMetaData = useRecoilValue(blockExportMetadata(id));

  const analysis = analyses?.[0]?.[0];
  const portfolioBreakdownResponse = analysis?.portfolioBreakdown?.[0];
  const hasNegativeAssets = portfolioBreakdownResponse?.hasNegativeAssets;

  if (infoGraphicType === 'PIE') {
    return (
      <Chart
        id={id}
        requests={requests}
        analyses={analyses}
        exportMetaData={{ ...exportMetaData, breakdownType: hasNegativeAssets ? 'Long Rescaled Values' : undefined }}
      />
    );
  }

  return null;
};

export interface ChartProps {
  id: string;
  requests: AnalysisRequest[];
  analyses: (Analysis | undefined)[][];
  exportMetaData: ExportMetaData;
}

const Chart = memo(function InternalChart({ id, requests, analyses, exportMetaData }: ChartProps) {
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const { blockRef: downloadableContentRef } = useBlockContext();

  const analyzedSubjects = useMemo(
    () => requests.map(({ subject }) => getAnalyzedSubjectFromRequestSubject(subject)),
    [requests],
  );
  const subjectColors = useSubjectColors(analyzedSubjects);
  const metrics = useSelectedMetrics();
  const selectedBlock = useRecoilValue(customizedBlock(id));
  const responseParser = useResponseParser(id);
  const { customBlockType } = useRecoilValue(blockSettings(id));

  return (
    <PieChart
      selectedRefId={id}
      requests={requests}
      analyses={analyses}
      selectedBlock={selectedBlock}
      exportMetaData={exportMetaData}
      subjectColors={subjectColors}
      metrics={metrics}
      responseParser={responseParser}
      inPrintMode={inPrintModeOrReportLab}
      customBlockType={customBlockType}
      downloadableContentRef={downloadableContentRef}
      hideSubject
    />
  );
});
